import { createStart, createCsrfMiddleware, createMiddleware } from "@tanstack/react-start";

import { renderErrorPage } from "./lib/error-page";

// Adresy z końcowym ukośnikiem (/mindmaster/one/) domyślnie dostają 307 na
// wariant bez ukośnika, a dopiero potem 301 — jeden trwały skok jest lepszy
// dla SEO, więc normalizujemy ścieżkę tutaj.
const LEGACY_PATHS: Record<string, string> = {
  "/index.html": "/",
  "/mm-one": "/konsultacje",
  "/mm-circle": "/mastermind",
  "/mindmaster/one": "/konsultacje",
  "/mindmaster/group": "/mastermind",
  "/mindmaster": "/konsultacje",
  "/praca-indywidualna": "/konsultacje",
  "/mindmaster-one": "/konsultacje",
  "/mindmaster-group": "/mastermind",
  // Stara strona WordPress
  "/psychoterapia": "/konsultacje",
  "/media": "/media-i-publikacje",
  "/youtube": "/media-i-publikacje",
  "/dziecinstwo-do-poprawki": "/media-i-publikacje",
  "/blog/dawno-dawno-temu": "/o-mnie",
  "/blog": "/psychologia-biznesu",
  // Konsolidacja Bazy wiedzy z Psychologią biznesu
  "/baza-wiedzy": "/psychologia-biznesu",
  // Scalenie z artykułem o wypaleniu
  "/wypalenie-zawodowe-liderow": "/wypalenie-zawodowe-u-lidera",
};

/** Domeny, z których kierujemy trwale (301) na domenę docelową. */
const LEGACY_HOSTS = new Set([
  "sylwia-sitkowska.lovable.app",
  "www.sylwia-sitkowska.lovable.app",
  "www.sylwiasitkowska.pl",
]);
const CANONICAL_ORIGIN = "https://sylwiasitkowska.pl";

const trailingSlashMiddleware = createMiddleware().server(async ({ next, request }) => {
  const url = new URL(request.url);
  if (url.pathname.startsWith("/lovable/")) {
    return next();
  }
  const clean = url.pathname.length > 1 ? url.pathname.replace(/\/+$/, "") || "/" : url.pathname;
  const target =
    LEGACY_PATHS[clean] ?? (clean.startsWith("/blog/") ? "/psychologia-biznesu" : clean);
  const host = (request.headers.get("x-forwarded-host") ?? url.host).toLowerCase();
  if (LEGACY_HOSTS.has(host)) {
    return new Response(null, {
      status: 301,
      headers: { location: `${CANONICAL_ORIGIN}${target}${url.search}` },
    });
  }
  if (target !== url.pathname) {
    return new Response(null, { status: 301, headers: { location: target + url.search } });
  }
  const response = await next();
  // Crawlery i podglądy społecznościowe dostają odpowiedź z brzegu (CDN),
  // dzięki czemu metatagi renderują się szybciej przy powtórnych pobraniach.
  const raw = response as unknown as { headers?: Headers };
  const contentType = raw.headers?.get("content-type") ?? "";
  if (
    request.method === "GET" &&
    contentType.includes("text/html") &&
    !raw.headers?.has("cache-control") &&
    !url.pathname.startsWith("/seo-")
  ) {
    // Strony z listą wydarzeń zmieniają się wraz z datą, więc trzymamy je na
    // brzegu krótko i wymuszamy szybkie odświeżenie po publikacji.
    const dateSensitive = url.pathname === "/" || url.pathname === "/wystapienia";
    raw.headers?.set(
      "cache-control",
      dateSensitive
        ? "public, max-age=0, s-maxage=60, must-revalidate, stale-while-revalidate=300"
        : "public, max-age=0, s-maxage=600, stale-while-revalidate=86400",
    );
  }

  return response;
});



const errorMiddleware = createMiddleware().server(async ({ next, request }) => {
  if (new URL(request.url).pathname.startsWith("/lovable/")) {
    return next();
  }
  try {
    return await next();
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      throw error;
    }
    console.error(error);
    return new Response(renderErrorPage(), {
      status: 500,
      headers: { "content-type": "text/html; charset=utf-8" },
    });
  }
});

// Start installs this automatically when src/start.ts is absent; defining the
// file opts out, so re-add it explicitly to keep server functions protected
// from cross-site requests.
const csrfMiddleware = createCsrfMiddleware({
  filter: (ctx) => ctx.handlerType === "serverFn",
});

export const startInstance = createStart(() => ({
  requestMiddleware: [trailingSlashMiddleware, errorMiddleware, csrfMiddleware],
}));
